import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  input,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import { ReportFmtPipe } from '../report-i18n.pipe';
import { GradeLevelRef, SubjectReportView } from '../report.model';
import { SubjectInsightsSection } from './subject-insights.section';
import { SubjectLevelsSection } from './subject-levels.section';
import { SubjectOverviewSection } from './subject-overview.section';
import { SubjectRingSection } from './subject-ring.section';
import { SubjectSchoolLevelsSection } from './subject-school-levels.section';
import { SubjectSkillsSection } from './subject-skills.section';
import { SubjectSummarySection } from './subject-summary.section';
import { SubjectWeeklySection } from './subject-weekly.section';

/**
 * Le rapport d'une matière — l'unité que décrit la maquette.
 *
 * Ce panneau est le document d'origine au complet : synthèse, acquisition,
 * table des compétences, niveaux scolaires, points forts et lacunes, barème
 * des paliers, anneau des lacunes, plan de remédiation. Rien d'autre. Les deux
 * blocs d'ensemble — « toutes les matières » et « comparaison des
 * performances » — vivent au niveau de la page, une fois, après le dernier
 * panneau : ils parlent de l'ensemble et n'auraient aucun sens répétés matière
 * par matière.
 *
 * Le rapport d'une matière et le rapport complet sont donc **le même
 * composant**, instancié une fois ou N fois. C'est ce qui garantit qu'ils ne
 * divergeront pas : il n'existe pas deux mises en page à maintenir.
 *
 * ── Visible, masqué, imprimé ───────────────────────────────────────────────
 * `visible` ne commande que l'écran, où l'on ne lit qu'une matière à la fois.
 * À l'impression, la feuille rétablit tous les panneaux : le PDF du rapport
 * complet contient chaque matière, chacune ouvrant sa propre page. Un panneau
 * masqué au tirage serait une matière manquante.
 */
@Component({
  selector: 's2e-report-subject-panel',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    ReportFmtPipe,
    SubjectSummarySection,
    SubjectOverviewSection,
    SubjectSkillsSection,
    SubjectSchoolLevelsSection,
    SubjectInsightsSection,
    SubjectLevelsSection,
    SubjectRingSection,
    SubjectWeeklySection,
  ],
  templateUrl: './subject-panel.component.html',
  host: {
    class: 'report-panel',
    '[class.is-hidden]': '!visible()',
    '[attr.id]': 'subject().anchorId',
  },
})
export class SubjectPanelComponent {
  readonly subject = input.required<SubjectReportView>();

  /** Référentiel des niveaux scolaires : l'axe du graphique de datation. */
  readonly scale = input.required<GradeLevelRef[]>();

  /** Nombre de semaines du plan, décidé par le back. */
  readonly planWeeks = input.required<number>();

  /** Faux pour les panneaux que l'écran n'affiche pas. Sans effet au tirage. */
  readonly visible = input(true);

  /**
   * Titre de matière en tête de panneau.
   *
   * Inutile quand le rapport ne couvre qu'une matière : l'en-tête l'annonce
   * déjà, et le répéter ferait doublon en haut de la première page.
   */
  readonly showTitle = input(false);
}
